import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MessageCircle, RefreshCw, Send } from "lucide-react";

/**
 * Admin-only Interakt / WhatsApp diagnostic page.
 *
 * Purpose: prove which WABA phone number is bound to the current
 * INTERAKT_API_KEY. If the sender number seen on the recipient's WhatsApp
 * matches +91 83176 74160, the key is correct. If it shows +91 94417 24072,
 * the key belongs to the wrong Interakt account and must be replaced in
 * backend/.env.
 */
export default function WhatsAppDiagnostics() {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [phone, setPhone] = useState("");
  const [template, setTemplate] = useState("_meta_lead_welcome");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const runHealth = async () => {
    setChecking(true);
    try {
      const r = await api.get("/admin/whatsapp/interakt-health");
      setHealth(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Health check failed");
    } finally {
      setChecking(false);
    }
  };

  const sendTest = async () => {
    if (!/^\+?\d[\d\s\-()]{9,14}$/.test(phone.trim())) {
      toast.error("Enter a valid phone with country code, e.g. +919999900001");
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const r = await api.post("/admin/whatsapp/interakt-test-send", {
        phone: phone.trim(),
        template_name: template.trim(),
        body_values: ["Admin", "https://neuroopinion.in"],
      });
      setSendResult(r.data);
      if (r.data.sent) toast.success("Sent — check the recipient's WhatsApp");
      else toast.error("Interakt rejected the send — see response below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="wa-diag-page">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-green-600" />
        <h1 className="font-heading text-2xl font-semibold">WhatsApp / Interakt Diagnostics</h1>
      </div>
      <p className="text-sm text-slate-600">
        Diagnose the &quot;welcome messages are coming from the wrong number&quot; issue.
        Interakt does not expose an API to fetch the WABA phone number bound to
        an API key, so the ground-truth is a live template send: whichever number
        shows up as the sender on the recipient&apos;s WhatsApp is the actual WABA.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>1. Config health</span>
            <Button size="sm" variant="outline" onClick={runHealth} disabled={checking} data-testid="wa-diag-health-btn">
              <RefreshCw className={"w-3.5 h-3.5 mr-1.5 " + (checking ? "animate-spin" : "")}/>
              {checking ? "Checking…" : "Run check"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!health && <div className="text-sm text-slate-500">Click &quot;Run check&quot; to verify Interakt API key + configured numbers.</div>}
          {health && (
            <div className="space-y-2 text-sm">
              <Row k="Interakt enabled" v={health.interakt_enabled} bool />
              <Row k="API key valid" v={health.api_key_valid} bool />
              <Row k="API key (masked)" v={health.api_key_masked || "—"} />
              <Row k="Public WhatsApp (patients see)" v={health.configured_public_whatsapp || "—"} highlight={health.configured_public_whatsapp !== "+918317674160"} />
              <Row k="Ops WhatsApp" v={health.configured_ops_whatsapp || "—"} highlight={health.configured_ops_whatsapp !== "+918317674160"} />
              <Row k="Auth-check HTTP status" v={health.auth_check_status || "—"} />
              {health.note && <div className="text-xs text-slate-500 border-l-2 border-slate-300 pl-2 mt-3">{health.note}</div>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">2. Send a live test template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-600">
            Fire a real Interakt template to your phone. Whatever number appears
            as the sender on WhatsApp is the actual WABA your API key is bound
            to. Expected: <b>+91 83176 74160</b>. If you see <b>+91 94417 24072</b>,
            the key must be replaced in <code>backend/.env</code>.
          </p>
          <div className="grid gap-3">
            <div>
              <Label>Your phone (with country code)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919999900001" className="mt-1" data-testid="wa-diag-phone"/>
            </div>
            <div>
              <Label>Template name</Label>
              <Input value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="meta_lead_welcome" className="mt-1" data-testid="wa-diag-template"/>
              <div className="text-[11px] text-slate-500 mt-1">
                Default: <code>meta_lead_welcome</code> — the exact template the user reports is arriving from the wrong number.
              </div>
            </div>
          </div>
          <Button onClick={sendTest} disabled={sending} className="bg-green-600 hover:bg-green-700" data-testid="wa-diag-send-btn">
            <Send className="w-4 h-4 mr-2"/>{sending ? "Sending…" : "Send test WhatsApp"}
          </Button>

          {sendResult && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                {sendResult.sent ? <CheckCircle2 className="w-4 h-4 text-green-600"/> : <XCircle className="w-4 h-4 text-red-600"/>}
                {sendResult.sent ? "Interakt accepted the send" : "Interakt rejected the send"}
              </div>
              <pre className="whitespace-pre-wrap break-words text-slate-700 font-mono text-[11px]" data-testid="wa-diag-result">
                {JSON.stringify(sendResult.provider_response, null, 2)}
              </pre>
              <div className="text-slate-600 mt-2">{sendResult.reminder}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">3. Send a plain-text (bot-style) message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-600">
            The bot doesn&apos;t use templates for replies — it sends free-form text within your active 24h session. If (2) works but this fails, the bot cannot function on your Interakt plan.
          </p>
          <Button
            className="bg-slate-800 hover:bg-slate-900"
            data-testid="wa-diag-text-btn"
            onClick={async () => {
              if (!phone) { toast.error("Enter a phone in section 2 first"); return; }
              try {
                const r = await api.post("/admin/whatsapp/interakt-test-text", { phone: phone.trim(), message: "Hi from bot — diagnostic ping" });
                setSendResult(r.data);
                if (r.data.sent) toast.success("Plain-text send OK — check WhatsApp");
                else toast.error("Interakt rejected plain text");
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }}
          >Send plain text to {phone || "…"}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>4. Recent inbound webhook events</span>
            <Button size="sm" variant="outline" data-testid="wa-diag-inbound-btn"
              onClick={async () => {
                try {
                  const r = await api.get("/admin/whatsapp/inbound-log?limit=20");
                  setSendResult({ sent: r.data.count > 0, provider_response: r.data, reminder: r.data.count === 0 ? "❌ ZERO webhook hits — Interakt is NOT calling our webhook. The block is on Interakt's side (webhook URL wrong, events not ticked, or Interakt AI intercepting)." : "✅ Interakt IS calling our webhook. If the bot still doesn't reply, the block is on the outbound send." });
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
            ><RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Fetch last 20</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-600">
            Send &quot;Hi&quot; to your WABA on WhatsApp, wait 5 seconds, then click Fetch. If the count is 0, Interakt never called our webhook.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const Row = ({ k, v, bool = false, highlight = false }) => {
  let display = v;
  if (bool) display = v ? "Yes" : "No";
  return (
    <div className={"flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 " + (highlight ? "bg-amber-50 -mx-2 px-2 rounded" : "")}>
      <div className="text-slate-600">{k}</div>
      <div className={"font-mono " + (bool && v ? "text-green-700" : bool ? "text-red-700" : "text-slate-900")}>{String(display)}</div>
    </div>
  );
};
